import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import RAPIER from '@dimforge/rapier3d-compat';
import { PHYSICS, CLICK, CAMERA, OBJECTS, OBJECT_SCALE, MOBILE } from './3d-config.js';

// ---------------------------------------------------------------------------
// Renderer + scene + camera
// ---------------------------------------------------------------------------
// sections/qs-hero-3D.liquid renders a bare <div data-qs-hero-3d> for this
// script to mount into — not an #app element, and not the full window. This
// script (unlike the others in this file's history) assumes a SINGLE
// instance per page, matching its original design.
const app = document.querySelector('[data-qs-hero-3d]');

// Robust viewport read — never return 0 (avoids NaN aspect ratios).
// Sized to the container, not the window, so the scene fills the hero area.
function viewport() {
  const rect = app.getBoundingClientRect();
  const w = rect.width || 1280;
  const h = rect.height || 800;
  return { w, h };
}

// Mobile: shrink every asset so the scene fits a narrow phone screen.
// Evaluated once at load (a full reload picks up orientation/size changes).
const IS_MOBILE = viewport().w <= MOBILE.breakpoint;
const OBJ_SCALE = OBJECT_SCALE / (IS_MOBILE ? MOBILE.divisor : 1);

const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
// Cap DPR lower on mobile — the chrome/PBR + physics combo is the heaviest
// part of the page, and phones rarely benefit from the full retina resolution.
renderer.setPixelRatio(Math.min(window.devicePixelRatio, IS_MOBILE ? 1.5 : 2));
renderer.setSize(viewport().w, viewport().h);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.92; // slightly lower => deeper shadows on chrome
app.appendChild(renderer.domElement);

const scene = new THREE.Scene();

// Bright studio environment => chrome reads as polished silver (not black).
// A light gradient dome for the overall tone + a few bright panels for the
// crisp specular highlights you expect on chrome.
function buildStudioEnvironment() {
  const envScene = new THREE.Scene();

  // Gradient dome (white top -> light grey bottom).
  const canvas = document.createElement('canvas');
  canvas.width = 16;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0.0, '#a7adb5'); // grey sky (darker)
  g.addColorStop(0.48, '#929aa2');
  g.addColorStop(0.6, '#767e87'); // horizon line
  g.addColorStop(0.62, '#3c424b'); // dark grey "floor" => deep chrome contrast
  g.addColorStop(1.0, '#22272e');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 16, 256);
  const domeTex = new THREE.CanvasTexture(canvas);
  domeTex.colorSpace = THREE.SRGBColorSpace;
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(60, 32, 16),
    new THREE.MeshBasicMaterial({ map: domeTex, side: THREE.BackSide })
  );
  envScene.add(dome);

  // Over-bright panels (color > 1) => sharp chrome highlights.
  const panel = (x, y, z, w, h, intensity) => {
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ color: 0xffffff })
    );
    m.material.color.multiplyScalar(intensity);
    m.position.set(x, y, z);
    m.lookAt(0, 0, 0);
    envScene.add(m);
  };
  // Large soft panels for overall lift.
  panel(22, 26, 12, 34, 12, 1.6);
  panel(-28, 12, 16, 22, 26, 1.2);
  panel(6, -22, -24, 44, 16, 0.9);
  panel(-10, 20, -26, 26, 26, 1.0);

  // Small, over-bright "spots" => crisp white chrome highlights (more shine).
  panel(12, 32, 20, 8, 8, 5.0);
  panel(-18, 26, 10, 6, 9, 4.5);
  panel(32, 6, 18, 7, 7, 4.5);
  panel(-8, 34, -12, 9, 6, 4.0);
  panel(18, -8, 24, 6, 6, 4.0);
  panel(-30, -4, 12, 6, 8, 3.5);
  panel(4, 14, 30, 5, 5, 5.0);

  return envScene;
}

const pmrem = new THREE.PMREMGenerator(renderer);
// Higher sigma = blurrier reflections => soft chrome glows instead of hard spots.
// Capped at 0.04: above that, PMREMGenerator's blur pass needs more samples than
// its internal max (20) and clips, spamming a console warning every load.
scene.environment = pmrem.fromScene(buildStudioEnvironment(), 0.04).texture;

// Orthographic camera => world coordinates map cleanly to window edges,
// and objects keep their size regardless of depth (matches the flat look).
let aspect = viewport().w / viewport().h;
const H = CAMERA.frustumHeight;
const camera = new THREE.OrthographicCamera(
  (-H * aspect) / 2, (H * aspect) / 2, H / 2, -H / 2, 0.1, 100
);
camera.position.set(0, 0, 30);
camera.lookAt(0, 0, 0);

// Lighting (env map does most of the work; keep ambient/fill low so shadows on
// the metal stay dark => stronger chrome contrast).
scene.add(new THREE.AmbientLight(0xffffff, 0.12));
const key = new THREE.DirectionalLight(0xffffff, 1.4);
key.position.set(4, 8, 10);
scene.add(key);
const fill = new THREE.DirectionalLight(0xffffff, 0.25);
fill.position.set(-6, -2, 6);
scene.add(fill);

// ---------------------------------------------------------------------------
// Materials
// ---------------------------------------------------------------------------
function makeMaterial(kind, color) {
  switch (kind) {
    case 'chrome':
      return new THREE.MeshStandardMaterial({
        color: color ?? 0xffffff,
        metalness: 1.0,
        roughness: 0.05,        // near-mirror => proper chrome
        envMapIntensity: 2.0,   // reflect the studio env brighter
      });
    case 'glass':
      return new THREE.MeshStandardMaterial({ color: color ?? 0xdff0ff, metalness: 0.1, roughness: 0.05, transparent: true, opacity: 0.55 });
    case 'matte':
    default:
      return new THREE.MeshStandardMaterial({ color: color ?? 0xffffff, metalness: 0.0, roughness: 0.7 });
  }
}

// Draw a placeholder "cutout" texture on a canvas (stands in for a PNG).
function makeLabelTexture(label, bg, fg) {
  const S = 512;
  const c = document.createElement('canvas');
  c.width = S; c.height = S;
  const ctx = c.getContext('2d');
  const r = 70;
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.roundRect(20, 20, S - 40, S - 40, r);
  ctx.fill();
  ctx.fillStyle = fg;
  ctx.font = 'bold 92px -apple-system, Helvetica, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, S / 2, S / 2);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

// ---------------------------------------------------------------------------
// Physics world  (initialized in boot() at the bottom — no top-level await,
// so the bundle builds for any target and is easy to import.)
// ---------------------------------------------------------------------------
let world;

const bodies = []; // { mesh, body, isFlat, mass }
const gltfLoader = new GLTFLoader();
const textureLoader = new THREE.TextureLoader();

// World bounds at the z=0 plane. zHalf may grow to fit the largest 3D object
// so nothing ever gets wedged between the front/back walls.
let zHalf = PHYSICS.zDepth;
function bounds() {
  return { halfW: (H * aspect) / 2, halfH: H / 2, halfZ: zHalf };
}

// ---------------------------------------------------------------------------
// Walls (fixed colliders just outside the visible frame)
// ---------------------------------------------------------------------------
const T = 5; // wall thickness (half-extent)
const walls = {};
function makeWall(name) {
  const bodyDesc = RAPIER.RigidBodyDesc.fixed();
  const body = world.createRigidBody(bodyDesc);
  walls[name] = body;
  return body;
}

function layoutWalls() {
  const { halfW, halfH, halfZ } = bounds();
  const big = 100;
  const place = (name, hx, hy, hz, px, py, pz) => {
    const b = walls[name];
    b.setTranslation({ x: px, y: py, z: pz }, true);
    // Reset collider: remove old, add sized one.
    while (b.numColliders() > 0) world.removeCollider(b.collider(0), false);
    const cd = RAPIER.ColliderDesc.cuboid(hx, hy, hz)
      .setRestitution(PHYSICS.restitution)
      .setFriction(PHYSICS.friction);
    world.createCollider(cd, b);
  };
  place('floor', big, T, big, 0, -halfH - T, 0);
  place('ceiling', big, T, big, 0, halfH + T, 0);
  place('left', T, big, big, -halfW - T, 0, 0);
  place('right', T, big, big, halfW + T, 0, 0);
  place('front', big, big, T, 0, 0, halfZ + T);
  place('back', big, big, T, 0, 0, -halfZ - T);
}

// ---------------------------------------------------------------------------
// Spawning objects
// ---------------------------------------------------------------------------
// Track where objects have been placed so new ones don't overlap.
const placed = []; // { x, y, r }
const SPAWN_MARGIN = 1.04; // extra gap between objects (1 = just touching)

function measureObject(obj) {
  const box = new THREE.Box3().setFromObject(obj);
  const size = box.getSize(new THREE.Vector3());
  return {
    radius: 0.5 * Math.max(size.x, size.y, size.z), // for X/Y placement spacing
    zHalf: 0.5 * size.z,                            // half-depth => needed Z clearance
  };
}

function randInBounds(r) {
  const { halfW, halfH, halfZ } = bounds();
  const bx = Math.max(0.1, halfW - r);
  const by = Math.max(0.1, halfH - r);
  const bz = Math.max(0, halfZ - r); // keep object fully inside the Z aquarium
  return {
    x: (Math.random() * 2 - 1) * bx,
    y: (Math.random() * 2 - 1) * by,
    z: (Math.random() * 2 - 1) * bz,
  };
}

// Find a spot that doesn't overlap anything already placed (in the X/Y plane —
// the Z aquarium is shallow, so X/Y separation is what prevents collisions).
// If the frame is crowded, return the LEAST-overlapping candidate found.
function nonOverlappingPos(r) {
  let best = null;
  let bestClearance = -Infinity; // negative = overlapping; larger = more room
  for (let attempt = 0; attempt < 600; attempt++) {
    const p = randInBounds(r);
    let minClearance = Infinity;
    for (const q of placed) {
      const dx = p.x - q.x;
      const dy = p.y - q.y;
      const dist = Math.hypot(dx, dy);
      const clearance = dist - (r + q.r) * SPAWN_MARGIN;
      if (clearance < minClearance) minClearance = clearance;
    }
    if (minClearance >= 0) return p;          // fully clear — take it
    if (minClearance > bestClearance) {       // otherwise remember the best try
      bestClearance = minClearance;
      best = p;
    }
  }
  return best; // crowded frame: the least-overlapping spot we found
}

function addDynamicBody(mesh, colliderDesc, { isFlat = false } = {}) {
  const { radius, zHalf: objZHalf } = measureObject(mesh); // mesh is still at the origin here
  const rbDesc = RAPIER.RigidBodyDesc.dynamic()
    .setTranslation(0, 0, 0) // provisional; layoutObjects() sets the real spot
    .setLinearDamping(PHYSICS.linearDamping)
    .setAngularDamping(PHYSICS.angularDamping)
    .setCanSleep(false);
  const body = world.createRigidBody(rbDesc);

  if (isFlat) {
    // Flat cutouts keep facing the camera (lock tilt on X/Y) but can spin
    // in-plane about Z — so they respond to physics instead of acting as
    // rigid, immovable barriers.
    body.setEnabledRotations(false, false, true, true);
  }

  colliderDesc
    .setRestitution(PHYSICS.restitution)
    .setFriction(PHYSICS.friction)
    .setDensity(1.0);
  world.createCollider(colliderDesc, body);

  scene.add(mesh);
  // Mass never changes after the collider's density is set — cache it once
  // here instead of crossing the WASM boundary for body.mass() every frame.
  const mass = body.mass() || 1;
  bodies.push({ mesh, body, isFlat, radius, zHalf: objZHalf, mass });
}

// Widen the Z aquarium (and rebuild walls) so the deepest object fits with room
// to move — prevents any object from getting wedged between front/back walls.
function fitZDepth() {
  let maxZ = 0;
  for (const b of bodies) if (b.zHalf > maxZ) maxZ = b.zHalf;
  const needed = Math.max(PHYSICS.zDepth, maxZ + 0.25);
  if (needed !== zHalf) {
    zHalf = needed;
    layoutWalls();
  }
}

// Place every object at a non-overlapping spot (largest first => best packing),
// then give it a gentle drift. Used on first load AND on reset, so objects never
// spawn inside one another (which is what caused the frenetic jitter).
function layoutObjects() {
  placed.length = 0;
  const d = PHYSICS.startDrift;
  const order = [...bodies].sort((a, b) => b.radius - a.radius); // big ones first
  for (const { mesh, body, isFlat, radius } of order) {
    const p = nonOverlappingPos(radius);
    placed.push({ x: p.x, y: p.y, r: radius });

    body.setTranslation({ x: p.x, y: p.y, z: p.z }, true);
    mesh.position.set(p.x, p.y, p.z);
    // Spawn axis-aligned so the bounding radius is exact (a randomly-rotated box
    // would poke its corners past that radius and get shoved by the walls).
    // Objects tumble naturally once they start drifting and colliding.
    body.setRotation({ x: 0, y: 0, z: 0, w: 1 }, true);
    body.setLinvel(
      { x: (Math.random() - 0.5) * d, y: (Math.random() - 0.5) * d, z: (Math.random() - 0.5) * d * 0.4 },
      true
    );
    body.setAngvel(
      isFlat
        ? { x: 0, y: 0, z: (Math.random() - 0.5) * d } // flats: gentle in-plane spin only
        : { x: (Math.random() - 0.5) * d, y: (Math.random() - 0.5) * d, z: (Math.random() - 0.5) * d },
      true
    );
  }
}

function randomQuat() {
  const e = new THREE.Euler(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
  const q = new THREE.Quaternion().setFromEuler(e);
  return { x: q.x, y: q.y, z: q.z, w: q.w };
}

function spawnPrimitive(cfg) {
  const mat = makeMaterial(cfg.material, cfg.color);
  let geometry, collider;
  const s = {};
  for (const k in cfg.size) s[k] = cfg.size[k] * OBJ_SCALE; // apply global size multiplier
  switch (cfg.geo) {
    case 'sphere':
      geometry = new THREE.SphereGeometry(s.r, 32, 24);
      collider = RAPIER.ColliderDesc.ball(s.r);
      break;
    case 'box':
      geometry = new THREE.BoxGeometry(s.x, s.y, s.z);
      collider = RAPIER.ColliderDesc.cuboid(s.x / 2, s.y / 2, s.z / 2);
      break;
    case 'cylinder':
      geometry = new THREE.CylinderGeometry(s.r, s.r, s.len, 40);
      collider = RAPIER.ColliderDesc.cylinder(s.len / 2, s.r);
      break;
    case 'cone':
      geometry = new THREE.ConeGeometry(s.r, s.len, 40);
      collider = RAPIER.ColliderDesc.cone(s.len / 2, s.r);
      break;
    case 'capsule':
    default:
      geometry = new THREE.CapsuleGeometry(s.r, s.len, 12, 24);
      collider = RAPIER.ColliderDesc.capsule(s.len / 2, s.r);
      break;
  }
  const mesh = new THREE.Mesh(geometry, mat);
  addDynamicBody(mesh, collider, { isFlat: false });
}

function buildFlat(tex, w, h) {
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, alphaTest: 0.5, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
  const depth = 0.18;
  const collider = RAPIER.ColliderDesc.cuboid(w / 2, h / 2, depth / 2);
  addDynamicBody(mesh, collider, { isFlat: true });
}

function spawnFlat(cfg) {
  if (cfg.url) {
    // Load the PNG, then size the plane from the image's real aspect ratio.
    // Use `fitH` (target height) or `fitW` (target width) in world units;
    // the other dimension is derived so the image never distorts.
    textureLoader.load(
      cfg.url,
      (tex) => {
        const aspect = (tex.image?.width || 1) / (tex.image?.height || 1);
        let w, h;
        if (cfg.fitH) { h = cfg.fitH * OBJ_SCALE; w = h * aspect; }
        else if (cfg.fitW) { w = cfg.fitW * OBJ_SCALE; h = w / aspect; }
        else { w = (cfg.w ?? 2) * OBJ_SCALE; h = (cfg.h ?? (cfg.w ?? 2) / aspect) * OBJ_SCALE; }
        buildFlat(tex, w, h);
        fitZDepth();
        layoutObjects();
        //console.log(`[flat] ${cfg.url} ${tex.image?.width}x${tex.image?.height} -> ${w.toFixed(2)}x${h.toFixed(2)}`);
      },
      undefined,
      (err) => console.error(`[flat] failed to load ${cfg.url}`, err)
    );
    return;
  }
  // Placeholder label card (explicit w/h).
  const tex = makeLabelTexture(cfg.label, cfg.bg, cfg.fg);
  buildFlat(tex, (cfg.w ?? 2) * OBJ_SCALE, (cfg.h ?? 1) * OBJ_SCALE);
}

// Collect a model's vertices expressed in `reference`'s local space, for a
// convex-hull collider. Sampled/strided so dense meshes stay cheap.
function collectHullPoints(object3d, reference) {
  reference.updateMatrixWorld(true);
  const invRef = new THREE.Matrix4().copy(reference.matrixWorld).invert();
  const v = new THREE.Vector3();
  const m = new THREE.Matrix4();
  const out = [];
  object3d.updateMatrixWorld(true);
  object3d.traverse((o) => {
    if (!o.isMesh || !o.geometry?.attributes?.position) return;
    m.multiplyMatrices(invRef, o.matrixWorld);
    const pos = o.geometry.attributes.position;
    const stride = Math.max(1, Math.floor(pos.count / 1500));
    for (let i = 0; i < pos.count; i += stride) {
      v.fromBufferAttribute(pos, i).applyMatrix4(m);
      out.push(v.x, v.y, v.z);
    }
  });
  return new Float32Array(out);
}

function spawnModel(cfg) {
  gltfLoader.load(
    cfg.url,
    (gltf) => {
      const root = gltf.scene;

      // Measure native size first (glTF from C4D can come in at any unit scale).
      const nativeBox = new THREE.Box3().setFromObject(root);
      const nativeSize = new THREE.Vector3();
      nativeBox.getSize(nativeSize);
      const maxDim = Math.max(nativeSize.x, nativeSize.y, nativeSize.z) || 1;

      // `fit` => auto-scale so the largest dimension equals `fit` world units.
      // Otherwise use an explicit `scale`. Then apply the effective scale.
      // `desktopScale` bumps size on desktop only (mobile keeps the base size).
      const base = cfg.fit ? cfg.fit / maxDim : (cfg.scale ?? 1);
      const desktopMul = !IS_MOBILE && cfg.desktopScale ? cfg.desktopScale : 1;
      const scale = base * OBJ_SCALE * desktopMul;
      root.scale.setScalar(scale);

      // Boost chrome: max out metalness + drop roughness on metallic parts so
      // they mirror the (blurred) studio env. On by default; set `chrome: false`
      // in config for a model that shouldn't be forced.
      const boost = cfg.chrome !== false;
      root.traverse((o) => {
        if (!o.isMesh || !o.material) return;
        const m = o.material;
        if (cfg.color && m.color) m.color.set(cfg.color); // optional color override
        if (boost && 'metalness' in m && (m.metalness ?? 0) >= 0.4) {
          // Chrome treatment (metallic assets only).
          if ('envMapIntensity' in m) m.envMapIntensity = 2.2;
          m.metalness = 1.0;
          m.roughness = Math.min(m.roughness ?? 0.1, 0.06);
        }
        m.needsUpdate = true;
      });

      // Recenter using the UNROTATED bounds (measuring after rotation would
      // inflate the box), then apply the fixed orientation.
      const box = new THREE.Box3().setFromObject(root);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);
      root.position.sub(center);

      if (cfg.rotate) {
        root.rotation.set(
          THREE.MathUtils.degToRad(cfg.rotate[0] || 0),
          THREE.MathUtils.degToRad(cfg.rotate[1] || 0),
          THREE.MathUtils.degToRad(cfg.rotate[2] || 0)
        );
      }

      const wrap = new THREE.Group();
      wrap.add(root);
      wrap.updateMatrixWorld(true);

      // Snug collider: a convex hull of the actual mesh so objects rest against
      // the model's real shape, not an oversized (rotation-inflated) box.
      // Falls back to a tight oriented box if the hull can't be built.
      let collider = null;
      try {
        const pts = collectHullPoints(root, wrap);
        if (pts.length >= 12) collider = RAPIER.ColliderDesc.convexHull(pts);
      } catch (e) {
        console.warn('[model] convex hull failed, using box', e);
      }
      let colliderKind = 'hull';
      if (!collider) {
        const q = new THREE.Quaternion().setFromEuler(root.rotation);
        collider = RAPIER.ColliderDesc.cuboid(size.x / 2, size.y / 2, size.z / 2)
          .setRotation({ x: q.x, y: q.y, z: q.z, w: q.w });
        colliderKind = 'oriented-box';
      }

      // Thin buffer around the hull => smooth contacts, no thin-shape jitter/glitch.
      let skinApplied = false;
      if (PHYSICS.contactSkin > 0 && typeof collider.setContactSkin === 'function') {
        collider.setContactSkin(PHYSICS.contactSkin);
        skinApplied = true;
      }

      addDynamicBody(wrap, collider, { isFlat: !!cfg.flat });
      fitZDepth();      // make sure the aquarium is deep enough for this model
      layoutObjects();  // re-place everything now that this model has joined
      //console.log(`[model] ${cfg.url} collider=${colliderKind} contactSkin=${skinApplied}`);
    },
    undefined,
    (err) => console.error(`[model] failed to load ${cfg.url}`, err)
  );
}

function spawnAll() {
  OBJECTS.forEach((cfg) => {
    const n = cfg.count ?? 1; // optional: spawn this object multiple times
    for (let i = 0; i < n; i++) {
      if (cfg.kind === 'flat') spawnFlat(cfg);
      else if (cfg.kind === 'model') spawnModel(cfg);
      else spawnPrimitive(cfg);
    }
  });
  fitZDepth();     // widen Z so the deepest object isn't wedged between walls
  layoutObjects(); // initial non-overlapping placement
}

// ---------------------------------------------------------------------------
// Click / tap => shake everything
// ---------------------------------------------------------------------------
function shake() {
  for (const { body, isFlat, mass } of bodies) {
    const horiz = 1 - CLICK.upBias; // less horizontal spread => cleaner vertical jump
    const dir = {
      x: (Math.random() - 0.5) * 2 * horiz,
      y: 0.75 + Math.random() * 0.9, // always a strong upward pop
      z: (Math.random() - 0.5) * 2 * horiz * 0.4,
    };
    body.applyImpulse(
      { x: dir.x * CLICK.strength * mass, y: dir.y * CLICK.strength * mass, z: dir.z * CLICK.strength * mass },
      true
    );
    if (!isFlat) {
      body.applyTorqueImpulse(
        {
          x: (Math.random() - 0.5) * CLICK.spin * mass,
          y: (Math.random() - 0.5) * CLICK.spin * mass,
          z: (Math.random() - 0.5) * CLICK.spin * mass,
        },
        true
      );
    } else {
      // Flats only spin about Z (stay facing camera).
      body.applyTorqueImpulse({ x: 0, y: 0, z: (Math.random() - 0.5) * CLICK.spin * mass }, true);
    }
  }
}
renderer.domElement.addEventListener('pointerdown', shake);

// ---------------------------------------------------------------------------
// Resize
// ---------------------------------------------------------------------------
function onResize() {
  // ResizeObserver fires immediately on .observe(), which can land before
  // boot()'s `await RAPIER.init()` has finished creating the world/walls.
  // Skip it here — boot() calls layoutWalls() itself once it's ready.
  if (!world) return;

  const { w, h } = viewport();
  aspect = w / h;
  camera.left = (-H * aspect) / 2;
  camera.right = (H * aspect) / 2;
  camera.top = H / 2;
  camera.bottom = -H / 2;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h);
  layoutWalls();
}
new ResizeObserver(onResize).observe(app);

// Pause the render loop while the hero is scrolled off-screen.
let running = true;
new IntersectionObserver((entries) => { running = entries[0].isIntersecting; }).observe(app);

// ---------------------------------------------------------------------------
// Render loop
// ---------------------------------------------------------------------------
const _q = new THREE.Quaternion();
function step() {
  // Continuous gentle wander so objects keep floating (never fully settle),
  // plus a speed clamp so nothing rockets away.
  const dt = world.timestep;
  for (const { body, mass: m } of bodies) {
    if (PHYSICS.wander > 0) {
      const w = PHYSICS.wander * m * dt;
      body.applyImpulse(
        { x: (Math.random() - 0.5) * w, y: (Math.random() - 0.5) * w, z: (Math.random() - 0.5) * w * 0.4 },
        true
      );
    }
    // Pull toward the z=0 plane (damped spring) so objects stay co-planar and
    // stack on top of each other instead of hiding in front of / behind others.
    if (PHYSICS.zCenter > 0) {
      const tz = body.translation().z;
      const vz = body.linvel().z;
      body.applyImpulse({ x: 0, y: 0, z: (-tz * PHYSICS.zCenter - vz * PHYSICS.zDamp) * m * dt }, true);
    }

    const v = body.linvel();
    const s = Math.hypot(v.x, v.y, v.z);
    if (s > PHYSICS.maxSpeed) {
      const k = PHYSICS.maxSpeed / s;
      body.setLinvel({ x: v.x * k, y: v.y * k, z: v.z * k }, true);
    } else if (PHYSICS.minSpeed > 0 && s < PHYSICS.minSpeed) {
      // Keep-alive: never let an object come to a dead stop.
      if (s < 1e-3) {
        const a = Math.random() * Math.PI * 2;
        body.setLinvel({ x: Math.cos(a) * PHYSICS.minSpeed, y: Math.sin(a) * PHYSICS.minSpeed, z: 0 }, true);
      } else {
        const k = PHYSICS.minSpeed / s;
        body.setLinvel({ x: v.x * k, y: v.y * k, z: v.z * k }, true);
      }
    }
  }

  world.step();
  for (const { mesh, body } of bodies) {
    const t = body.translation();
    mesh.position.set(t.x, t.y, t.z);
    // Copy rotation for everything. Flats are constrained to Z-only rotation,
    // so this just spins them in-plane while they keep facing the camera.
    const r = body.rotation();
    _q.set(r.x, r.y, r.z, r.w);
    mesh.quaternion.copy(_q);
  }
  renderer.render(scene, camera);
}
function tick() {
  if (running) step();
  requestAnimationFrame(tick);
}

// ---------------------------------------------------------------------------
// Boot — async init keeps top-level await out of the module.
// ---------------------------------------------------------------------------
async function boot() {
  await RAPIER.init();
  world = new RAPIER.World({ x: 0, y: PHYSICS.gravity, z: 0 });
  world.timestep = 1 / 60;

  ['floor', 'ceiling', 'left', 'right', 'front', 'back'].forEach(makeWall);
  layoutWalls();
  spawnAll();
  tick();

  // Debug hooks (harmless; handy for tweaking from the console).
  window.__scene = scene;
  window.__bodies = bodies;
  window.__camera = camera;
  window.__bounds = bounds;
  window.__world = world;
  window.__step = (n = 1) => { for (let i = 0; i < n; i++) step(); };
  window.__reset = layoutObjects; // force a reset without reloading — run __reset() in the console
}
boot();
